import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  School,
  TrendingUp,
  Assessment,
  Group,
  CheckCircle,
  Star,
  Security,
  Support,
  ArrowForward,
  Person,
  FamilyRestroom,
  Psychology,
  AutoAwesome,
  Speed,
  Analytics,
  EmojiEvents,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const JoinUsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <AutoAwesome sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'AI-Powered Learning',
      description: 'Personalized content and adaptive learning paths tailored to your unique needs',
      benefits: ['Smart content curation', 'Adaptive exercises', 'Real-time feedback', 'Progress optimization'],
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Self-Paced Learning',
      description: 'Learn at your own speed with flexible schedules and mastery-based progression',
      benefits: ['Flexible schedules', 'Pause and replay', 'Mastery-based learning', 'Confidence building'],
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Comprehensive Tracking',
      description: 'Monitor your progress across the Scottish curriculum with detailed analytics',
      benefits: ['Detailed reports', 'Goal setting', 'Visual progress mapping', 'Achievement celebration'],
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Exam Preparation',
      description: 'Prepare for competitive exams with expert-curated content and practice tests',
      benefits: ['Exam-specific materials', 'Practice tests', 'Performance benchmarking', 'Expert guidance'],
    },
  ];

  const userTypes = [
    {
      icon: <Person sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Students',
      description: 'Track your learning progress and achieve your educational goals',
      benefits: [
        'Personalized learning paths',
        'Progress tracking and analytics',
        'Interactive exercises and assessments',
        'Goal setting and achievement tracking',
      ],
    },
    {
      icon: <FamilyRestroom sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Parents',
      description: 'Monitor your child\'s educational journey and support their growth',
      benefits: [
        'Real-time progress monitoring',
        'Detailed performance reports',
        'Communication with teachers',
        'Support for learning at home',
      ],
    },
    {
      icon: <School sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Teachers',
      description: 'Manage your classes and help students reach their full potential',
      benefits: [
        'Class management tools',
        'Student progress monitoring',
        'Assessment and feedback tools',
        'Curriculum alignment support',
      ],
    },
    {
      icon: <Psychology sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Tutors',
      description: 'Provide personalized guidance and support to individual students',
      benefits: [
        'Individual student focus',
        'Custom learning plans',
        'Progress tracking tools',
        'Flexible scheduling options',
      ],
    },
  ];

  const stats = [
    { number: '50,000+', label: 'Active Students', icon: <Person /> },
    { number: '200+', label: 'Partner Schools', icon: <School /> },
    { number: '95%', label: 'Satisfaction Rate', icon: <Star /> },
    { number: '24/7', label: 'Support Available', icon: <Support /> },
  ];

  const courseTypes = [
    { name: 'Secondary School', status: 'active', description: 'Full functionality available' },
    { name: 'Primary School', status: 'coming-soon', description: 'In development' },
    { name: 'Piano', status: 'coming-soon', description: 'Music education' },
    { name: 'Chess', status: 'coming-soon', description: 'Strategic thinking' },
    { name: 'Music', status: 'coming-soon', description: 'Comprehensive music' },
    { name: 'Art', status: 'coming-soon', description: 'Creative arts' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Join the Future of Education
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Experience personalized learning powered by AI, track your progress across the Scottish curriculum, 
              and achieve your educational goals with our comprehensive platform.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                Start Free Trial
              </Button>
              <Button
                component={Link}
                to="/guest"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Try as Guest
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Watch Demo
              </Button>
            </Box>
          </Box>

          {/* Stats */}
          <Grid container spacing={4} sx={{ mt: 6, justifyContent: 'center' }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 1, opacity: 0.8 }}>
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 3,
              }}
            >
              Why Choose StudyStride?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Our platform combines cutting-edge technology with proven educational methods 
              to deliver exceptional learning experiences.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index} sx={{ minHeight: '100%' }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 400,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, textAlign: 'center', lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                    <List dense sx={{ flexGrow: 1 }}>
                      {feature.benefits.map((benefit, benefitIndex) => (
                        <ListItem key={benefitIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* User Types Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 3,
              }}
            >
              Designed for Everyone
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Whether you're a student, parent, teacher, or tutor, our platform provides 
              the tools you need to succeed.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
            {userTypes.map((userType, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ minHeight: '100%' }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    minHeight: 350,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {userType.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {userType.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {userType.description}
                    </Typography>
                    <List dense>
                      {userType.benefits.map((benefit, benefitIndex) => (
                        <ListItem key={benefitIndex} sx={{ px: 0, textAlign: 'left' }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Course Types Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 3,
              }}
            >
              Available Course Types
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              From traditional school subjects to specialized skills, 
              we're expanding our offerings to meet diverse learning needs.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {courseTypes.map((course, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index} sx={{ minHeight: '100%' }}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    opacity: course.status === 'coming-soon' ? 0.7 : 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 200,
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {course.name}
                    </Typography>
                    <Chip
                      label={course.status === 'active' ? 'Available' : 'Coming Soon'}
                      color={course.status === 'active' ? 'success' : 'warning'}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flexGrow: 1 }}
                    >
                      {course.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 3,
              }}
            >
              Ready to Start Your Journey?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              Join thousands of students, parents, and teachers who are already 
              experiencing the benefits of personalized learning with StudyStride.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                Get Started Free
              </Button>
              <Button
                component={Link}
                to="/guest"
                variant="outlined"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                Try as Guest
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default JoinUsPage;
